/**
 * 复习历史与间隔推进。真源只有一处：追加式日志，CardState 与 dueOn 都是它的派生值，
 * 所以不存在「改了状态忘了改到期日」这类同步问题。卡片不在状态表里就是新卡，没有 phase 标签。
 * 只用 model，被 plan 与 workspace 调用，ports 的日志形状取自这里的 Review。
 * 不挑今天复习谁，也不写日志。选卡在 plan，落盘在 ports。
 */

import type { Score } from '../model/result'
import { FULL } from '../model/result'
import type { CardKey, CardRef, DeviceId, EpochDay, IntervalDays, ReviewId } from '../model/ids'
import { addDays, cardKey, daysBetween, interval } from '../model/ids'

/** 四档评分，对应卷面上「完全没想起来 / 想起来了但很费力 / 正常 / 轻松」。 */
export type Rating = 'again' | 'hard' | 'good' | 'easy'

export const RATINGS: readonly Rating[] = ['again', 'hard', 'good', 'easy']

/**
 * 一行复习记录。落盘格式与内存对象同一个形状，折叠函数是它唯一的消费者。
 * 日志永不压缩、永不删卡，所以统计与重放永远能回到同一份历史。
 */
export interface Review {
	readonly id: ReviewId
	readonly card: CardKey
	readonly rating: Rating
	/** 客观题的得分。自评卡与知识点卡为 null，因为分数不是它们的结论。 */
	readonly score: Score | null
	readonly reviewedOn: EpochDay
	readonly reviewedAtMs: number
	readonly device: DeviceId
}

export interface CardState {
	readonly reps: number
	readonly lapses: number
	readonly intervalDays: IntervalDays
	readonly ease: number
	readonly lastReviewedOn: EpochDay
}

export type States = ReadonlyMap<CardKey, CardState>

/** 调参集中在这一个对象里。算法换实现时这里是唯一的接缝，所以不做可插拔策略接口。 */
export const TUNING = {
	firstIntervalDays: 1,
	secondIntervalDays: 6,
	minEase: 1.3,
	startEase: 2.5,
	hardFactor: 1.2,
	easyFactor: 1.3,
	againEasePenalty: 0.2,
	hardEasePenalty: 0.15,
	easyEaseBonus: 0.15,
} as const

export interface FoldResult {
	readonly states: States
	/** 已提交过的幂等键。submit 重放同一 submissionId 时靠它返回旧结论，不重复推进。 */
	readonly seenSubmissions: ReadonlySet<ReviewId>
	/** 知识仓库已删掉但日志还留着的卡，诊断屏提示用户清理。系统不自动删历史。 */
	readonly orphanKeys: readonly CardKey[]
}

/**
 * 客观题分数到评分档。easy 只留给用户自评，自动评分永远不给 easy，
 * 否则满分新卡和满分老卡会被同等放大。
 */
export function ratingFromScore(score: Score): Rating {
	if (score === FULL) return 'good'
	return score >= 0.5 ? 'hard' : 'again'
}

/**
 * 一次复习怎样改变一张卡。prev 为 undefined 表示第一次见到这张卡。
 * 纯函数，today 由调用方给，测试可以喂任意日期。
 * 前两次复习走固定阶梯（1、6 天），第三次起才乘因子：新卡的 ease 还没有任何证据支撑，
 * 让它一上来就参与乘法会把没复习过的卡和复习过二十次的卡排到同一天。
 * 间隔用本次调整之前的 ease，与 ease 更新各算一次，避免同一次复习影响自己的间隔两次。
 */
export function advance(prev: CardState | undefined, rating: Rating, today: EpochDay): CardState {
	const ease = prev?.ease ?? TUNING.startEase
	const lapses = prev?.lapses ?? 0
	if (rating === 'again') {
		return {
			reps: 0,
			lapses: lapses + 1,
			intervalDays: interval(TUNING.firstIntervalDays),
			ease: easeAfter(ease, -TUNING.againEasePenalty),
			lastReviewedOn: today,
		}
	}
	const reps = (prev?.reps ?? 0) + 1
	const ladder = reps === 1 ? TUNING.firstIntervalDays : reps === 2 ? TUNING.secondIntervalDays : null
	const factor = rating === 'hard' ? TUNING.hardFactor : rating === 'easy' ? ease * TUNING.easyFactor : ease
	const delta = rating === 'hard' ? -TUNING.hardEasePenalty : rating === 'easy' ? TUNING.easyEaseBonus : 0
	return {
		reps,
		lapses,
		intervalDays: interval(Math.max(1, ladder ?? Math.round((prev?.intervalDays ?? 0) * factor))),
		ease: easeAfter(ease, delta),
		lastReviewedOn: today,
	}
}

/** ease 只进乘法，保留两位就够；留浮点尾数的话同一份历史在不同引擎上会 fold 出不同的到期日。 */
function easeAfter(ease: number, delta: number): number {
	return Math.round(Math.max(TUNING.minEase, ease + delta) * 100) / 100
}

/** 到期日永远派生，不落盘。 */
export function dueOf(state: CardState): EpochDay {
	return addDays(state.lastReviewedOn, state.intervalDays)
}

/** 逾期天数，队列排序用。未到期为 0。 */
export function overdueDays(state: CardState, today: EpochDay): number {
	return Math.max(0, daysBetween(dueOf(state), today))
}

/**
 * 折叠日志。同 id 重复出现只算一次（双击、崩溃后重放、日志被重复读）。
 * 同一张卡取 reviewedAtMs 最大的那条作为状态来源，历史仍全部保留给统计。
 * 这条规则就是二期多端合并：设备各写各的日志，读的时候折叠到一起。
 * 每张卡的状态是它自己那条时间线上逐条 advance 的结果，所以 reps、lapses、ease 都只有一份算法在算。
 */
export function foldReviews(log: readonly Review[], knownCards: ReadonlySet<CardKey>): FoldResult {
	const seenSubmissions = new Set<ReviewId>()
	const ordered: Review[] = []
	for (const review of log) {
		if (seenSubmissions.has(review.id)) continue
		seenSubmissions.add(review.id)
		ordered.push(review)
	}
	// 同毫秒的并列保留日志顺序，Array.prototype.sort 是稳定排序，所以重放结果不会变。
	ordered.sort((a, b) => a.reviewedAtMs - b.reviewedAtMs)
	const states = new Map<CardKey, CardState>()
	for (const review of ordered) states.set(review.card, advance(states.get(review.card), review.rating, review.reviewedOn))
	const orphanKeys = [...states.keys()].filter((key) => !knownCards.has(key))
	return { states, seenSubmissions, orphanKeys }
}

/** 用当前 CardRef 取状态，调用方不需要知道前缀规则。缺卡即新卡。 */
export function stateFor(states: States, ref: CardRef): CardState | undefined {
	return states.get(cardKey(ref))
}

/** 今日已完成张数。从状态表算，所以进度条和队列永远同源。 */
export function reviewedToday(states: States, today: EpochDay): number {
	let done = 0
	for (const state of states.values()) if (state.lastReviewedOn === today) done += 1
	return done
}
